import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { contactInfo } from '../data/mock';

export const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* About Section */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_elevated-apply/artifacts/myyz19wz_transparent%20logo.png" 
              alt="Elevated Education logo"
              loading="lazy"
              decoding="async"
              className="h-16 w-auto mb-4"
            />
            <p className="text-sm text-gray-400 mb-4">
              Your trusted partner in achieving academic excellence — guiding Canadian students through their career college journey across BC and beyond.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.facebook.com/profile.php?id=61589064521819"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Elevated Education on Facebook"
                data-testid="footer-social-facebook"
                className="text-gray-400 hover:text-blue-500 transition-colors duration-200"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/info.elevatededucation/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Elevated Education on Instagram"
                data-testid="footer-social-instagram"
                className="text-gray-400 hover:text-blue-500 transition-colors duration-200"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm"
                >
                  Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('programs')}
                  className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm"
                >
                  Programs
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('partners')}
                  className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm"
                >
                  Partner Institutions
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('testimonials')}
                  className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm"
                >
                  Success Stories
                </button>
              </li>
              <li>
                <Link 
                  to="/blog"
                  className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm"
                  data-testid="footer-blog-link"
                >
                  Blog & Resources
                </Link>
              </li>
              <li>
                <a
                  href="/sitemap.xml"
                  className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm"
                  data-testid="footer-sitemap-link"
                >
                  Sitemap
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/services/college-admissions" className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm">
                  College Admissions
                </Link>
              </li>
              <li>
                <Link to="/services/career-path-planning" className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm">
                  Career Path Planning
                </Link>
              </li>
              <li>
                <Link to="/services/domestic-student-support" className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm">
                  Domestic Student Support
                </Link>
              </li>
              <li>
                <Link to="/services/application-assistance" className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm">
                  Application Assistance
                </Link>
              </li>
              <li>
                <Link to="/services/scholarship-guidance" className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm">
                  Scholarship Guidance
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <a href={`mailto:${contactInfo.email}`} className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <a href={`tel:${contactInfo.phone}`} className="text-gray-400 hover:text-blue-500 transition-colors duration-200 text-sm">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  {contactInfo.address}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} ElevatED Education. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-blue-500 transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-blue-500 transition-colors duration-200">
                Terms of Service
              </Link>
              <Link to="/disclaimer" className="text-sm text-gray-400 hover:text-blue-500 transition-colors duration-200">
                Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
